import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import { Router, RouterLink, RouterOutlet } from '@angular/router';
import { SHARED_IMPORTS } from 'src/app/shared/standalone-imports';
import { LayoutBreakpointService } from '@app/core/layout/layout-breakpoint.service';

interface GostWorkspaceTab {
  readonly path: 'overview' | 'progress' | 'records/new_gost';
  readonly icon: string;
  readonly label: string;
}

@Component({
  selector: 'app-gost-workspace',
  templateUrl: './gost-workspace.component.html',
  styleUrls: ['./gost-workspace.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [...SHARED_IMPORTS, RouterOutlet, RouterLink],
  host: {
    style: 'display: flex; flex-direction: column; flex: 1; min-height: 0;',
  },
})
export class GostWorkspaceComponent {
  private readonly layoutBreakpoint = inject(LayoutBreakpointService);
  private readonly router = inject(Router);

  readonly isHandset = this.layoutBreakpoint.isHandset;
  readonly submenuEntered = signal(false);

  readonly tabs: GostWorkspaceTab[] = [
    {
      path: 'overview',
      icon: 'dashboard',
      label: $localize`:@@home.gostWorkspace.tab.overview:レポート`,
    },
    {
      path: 'progress',
      icon: 'grid_view',
      label: $localize`:@@home.gostWorkspace.tab.matrix:経過表`,
    },
    {
      path: 'records/new_gost',
      icon: 'edit_note',
      label: $localize`:@@home.gostWorkspace.tab.manual:手入力`,
    },
  ];

  constructor() {
    afterNextRender(() => {
      this.submenuEntered.set(true);
    });
  }

  isTabActive(path: GostWorkspaceTab['path']): boolean {
    const url = this.router.url;
    const isOverview = /\/gost\/overview(?:\/|$)/.test(url);
    const isProgress = /\/gost\/progress(?:\/|$)/.test(url);
    const isDetail = this.isGostRecordDetail(url);
    const detailSource = this.getGostDetailSourceTab();
    const isManual = /\/gost\/records\/new_gost(?:\/|$)/.test(url) || /\/gost\/manual(?:\/|$)/.test(url);

    switch (path) {
      case 'overview':
        return isOverview || (isDetail && detailSource === 'overview');
      case 'progress':
        return isProgress || (isDetail && detailSource === 'progress');
      case 'records/new_gost':
        return isManual;
      default:
        return false;
    }
  }

  private isGostRecordDetail(url: string): boolean {
    return /\/gost\/records\/(?!new_gost(?:\/|$))[^/]+(?:\/|$)/.test(url);
  }

  private getGostDetailSourceTab(): 'overview' | 'progress' {
    const from = this.router.parseUrl(this.router.url).queryParams['from'];
    return from === 'overview' ? 'overview' : 'progress';
  }
}
